/** Helpers shared by the two Command Center flows. */

export type Level = "rec" | "guard" | "block";

export const LEVEL: Record<Level, { t: string; color: string; bg: string }> = {
  rec:   { t: "Recommended", color: "var(--success-card)", bg: "rgba(16,185,129,.1)" },
  guard: { t: "Guardrail",   color: "var(--warning)",      bg: "rgba(245,158,11,.1)" },
  block: { t: "Blocker",     color: "var(--error)",        bg: "rgba(239,68,68,.1)" },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Parse "YYYY-MM-DD" as a UTC date (no timezone drift). */
export const parseDay = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return y ? new Date(Date.UTC(y, (m || 1) - 1, d || 1)) : null;
};

export const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);

export const fmtDay = (d: Date | null) => (d ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` : "—");

export const fmtDayYear = (d: Date | null) => (d ? `${fmtDay(d)}, ${d.getUTCFullYear()}` : "—");

/** Tests created before the decision became free text stored one of these keys. */
const LEGACY_DECISIONS: Record<string, string> = {
  scale: "Should we increase this channel’s budget?", keep: "Is current spend worth keeping?", newc: "Does a new channel or creative work?",
};
export const decisionText = (v?: string | null) => LEGACY_DECISIONS[v || ""] ?? (v || "").trim();

export const todayIso = () => {
  const n = new Date();
  return isoDay(new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())));
};

/** Next Monday at least `days` from today — a sensible default flight start. */
export const defaultStart = (days = 21) => {
  let d = addDays(parseDay(todayIso()) as Date, days);
  while (d.getUTCDay() !== 1) d = addDays(d, 1);
  return isoDay(d);
};

export const quarterOf = (d: Date | null) => (d ? `Q${Math.floor(d.getUTCMonth() / 3) + 1}` : "Q4");

export const money = (k: number) => (k >= 1000 ? "$" + (k / 1000).toFixed(2) + "M" : "$" + (k >= 10 ? Math.round(k) : k.toFixed(1)) + "K");

export const dollars = (v: number) =>
  Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : Math.abs(v) >= 1e3 ? `$${Math.round(v / 1e3)}K` : `$${Math.round(v)}`;

export type Tone = "red" | "blue" | "amber" | "violet" | "green" | "grey";

export const TONE: Record<Tone, { color: string; bg: string }> = {
  red:    { color: "var(--error)",          bg: "rgba(239,68,68,.1)" },
  blue:   { color: "var(--blue)",           bg: "rgba(59,130,246,.1)" },
  amber:  { color: "var(--warning)",        bg: "rgba(245,158,11,.1)" },
  violet: { color: "var(--violet)",         bg: "rgba(139,92,246,.1)" },
  green:  { color: "var(--success-card)",   bg: "rgba(16,185,129,.1)" },
  grey:   { color: "var(--text-secondary)", bg: "rgba(148,163,184,.1)" },
};

export const CHANNELS = ["CTV", "Meta", "Search", "Social", "Display", "Audio", "Other"];

/** Failed guardrails for a test, as returned with the test list. */
export interface CheckSummary { block: number; guard: number; first_block: string | null }

/** What a test card on "All tests" shows. */
export interface CardInfo {
  badge: string;
  tone: Tone;
  bucket: "action" | "active" | "closed";
  segDone: number;
  segCur: boolean;
  chips: string[];
  next: string;
  nextWarn?: boolean;
  result?: { lift: string; eff: string; effLabel: string; bar: string; met: boolean; decision: string };
}

export const DECISION_LABEL: Record<string, string> = {
  scale: "increase budget",
  roll: "roll out to all markets",
  keep: "keep current spend",
  retest: "retest",
  stop: "stop this spend",
};

/** Market key from the GeoLift pipeline → display name. */
export const marketName = (key: string, names?: Record<string, string>) =>
  names?.[key] || key.replace(/\b\w/g, c => c.toUpperCase());

/**
 * Measurement happens mid-flight now: the old "Measure" stage (5) and the "data due"
 * phase (3) are gone, so Decide is stage 5. Tests saved before that carry no flowV;
 * shift their stages down and cap the phase. `raw` is the state as stored.
 */
export const FLOW_V = 2;
export function migrateFlow<S extends { stage: number; maxStage: number; phase: number; flowV?: number }>(raw: unknown, s: S): S {
  if ((raw as { flowV?: number } | null)?.flowV === FLOW_V) return s;
  const down = (n: number) => (n >= 5 ? n - 1 : n);
  return { ...s, stage: down(s.stage), maxStage: down(s.maxStage), phase: Math.min(s.phase, 2), flowV: FLOW_V };
}

/** How a mid-flight read reads against the plan's success bar (point estimate only). */
export const onTrack = (rev: boolean, iroas: number | null | undefined, lift: number | null | undefined, bar: number, cntBar: number) =>
  rev ? (iroas ?? 0) >= bar - 1e-9 : (lift ?? 0) >= cntBar;
